import { useEffect, useReducer } from 'react'
import api from '@api'
import {
  IPortfolioTooltipDepartment,
  IPortfolioTooltipSpaceUsageType,
  IPortfolioTooltipBuilding,
} from '@api/types'

enum Actions {
  LOAD_BUILDING,
  LOAD_FLOORS,
  RESET_UNMOUNTED,
}

export interface ITooltipByUsageType {
  areaSqft: number
  areaSqrm: number
  spacesCount: number
  usageType: string
}

export interface ITooltipByUnoccupiedInDepartments {
  name: string
  color: string
  unoccupied: number
}

export interface ITooltipByAllocatedInDepartments {
  name: string
  allocated: number
  unoccupied: number
  occupied: number
  department: {
    name: string
  }
}

export interface ITooltipByEmployeesInDepartment {
  name: string
  color: string
  total: number
}

export interface ITooltipByDepartmentsArea {
  name: string
  areaSqrm: number
  areaSqft: number
}

export interface ITooltipData {
  byUsageType: ITooltipByUsageType[]
  byUnoccupiedInDepartments: ITooltipByUnoccupiedInDepartments[]
  byAllocatedInDepartments: ITooltipByAllocatedInDepartments[]
  byEmployeesInDepartment: ITooltipByEmployeesInDepartment[]
  byDepartmentsArea: ITooltipByDepartmentsArea[]
}

export interface ITooltip {
  [key: string]: ITooltipData
}

interface IState {
  floors: ITooltip
  buildings: ITooltip
  unmounted: boolean
}

export interface IPortfolioTooltip extends IState {
  getBuilding: (buildingId: string, assignmentPlanId: string) => void
}

let persistedState: IState = {
  floors: {},
  buildings: {},
  unmounted: false,
}

interface LoadBuilding {
  type: Actions.LOAD_BUILDING
  building: ITooltipData
  buildingId: string
}

interface LoadFloors {
  type: Actions.LOAD_FLOORS
  floors: ITooltip
}

interface ResetUnmounted {
  type: Actions.RESET_UNMOUNTED
}

type PortfolioTooltipActionTypes = LoadBuilding | LoadFloors | ResetUnmounted

function reducer(state: IState, action: PortfolioTooltipActionTypes) {
  switch (action.type) {
    case Actions.LOAD_BUILDING:
      return {
        ...state,
        buildings: {
          ...state.buildings,
          [action.buildingId]: action.building,
        },
      }
    case Actions.LOAD_FLOORS:
      return {
        ...state,
        floors: {
          ...state.floors,
          ...action.floors,
        },
      }
    case Actions.RESET_UNMOUNTED:
      return {
        ...state,
        unmounted: false,
      }
    default:
      return state
  }
}

export default function usePortfolioTooltips(): IPortfolioTooltip {
  const [state, dispatch] = useReducer(reducer, persistedState)

  useEffect(() => {
    persistedState = state
  })

  useEffect(() => {
    persistedState = state

    return () => {
      persistedState = { ...state, unmounted: true }
    }
  })

  return {
    ...state,
    getBuilding: (buildingId: string, assignmentPlanId: string) => {
      if (state.buildings[buildingId] && !state.unmounted) return

      api.portfolio
        .tooltipBuilding({ id: buildingId, assignmentPlanId })
        .then(({ node: { portfolio = {} } }: any) => {
          if (state.unmounted) {
            dispatch({
              type: Actions.RESET_UNMOUNTED,
            })
          }
          dispatch({
            type: Actions.LOAD_FLOORS,
            floors: transformToFloors(portfolio),
          })
          dispatch({
            type: Actions.LOAD_BUILDING,
            building: transformToBuilding(portfolio),
            buildingId,
          })
        })
    },
  }
}

function transformToBuilding({
  spaceUsageTypes = [],
  departments = [],
}: IPortfolioTooltipBuilding): ITooltipData {
  return {
    byUsageType: spaceUsageTypes.sort(compareWorkstations).map((s) => ({
      usageType: s.readableType,
      areaSqft: s.areaSqft,
      areaSqrm: s.areaSqrm,
      spacesCount: s.workstations,
    })),
    byUnoccupiedInDepartments: departments.sort(compareDepartmentsUnoccupied).map((d) => ({
      unoccupied: d.unoccupied,
      name: d.department?.name,
      color: d.department?.color,
    })),
    byAllocatedInDepartments: departments.sort(compareDepartmentsAllocated).map((d) => ({
      unoccupied: d.unoccupied,
      name: d.department?.name,
      occupied: d.occupied,
      allocated: d.allocated,
      department: {
        name: d.department?.name,
      },
    })),
    byEmployeesInDepartment: departments.sort(compareDepartmentsEmployeesTotal).map((d) => ({
      name: d.department?.name,
      color: d.department?.color,
      total: d.seatedCount,
    })),
    byDepartmentsArea: departments
      .sort(compareDepartmentsArea)
      .filter((d) => d.areaSqft)
      .map((d) => ({
        name: d.department?.name,
        areaSqft: d.areaSqft,
        areaSqrm: d.areaSqrm,
      })),
  }
}

function transformToFloors(buildingPortfolio: IPortfolioTooltipBuilding): ITooltip {
  const result = buildingPortfolio?.floors?.reduce((res, floor) => {
    const newFloor = {
      byUsageType: floor.spaceUsageTypes.sort(compareWorkstations).map((s) => ({
        usageType: s.readableType,
        areaSqft: s.areaSqft,
        areaSqrm: s.areaSqrm,
        spacesCount: s.workstations,
      })),
      byUnoccupiedInDepartments: floor.departments.sort(compareDepartmentsUnoccupied).map((d) => ({
        unoccupied: d.unoccupied,
        name: d.department?.name,
        color: d.department?.color,
      })),
      byAllocatedInDepartments: floor.departments.sort(compareDepartmentsAllocated).map((d) => ({
        unoccupied: d.unoccupied,
        name: d.department?.name,
        occupied: d.occupied,
        allocated: d.allocated,
        department: {
          name: d.department?.name,
        },
      })),
      byEmployeesInDepartment: floor.departments
        .sort(compareDepartmentsEmployeesTotal)
        .map((d) => ({
          name: d.department?.name,
          color: d.department?.color,
          total: d.seatedCount,
        })),
      byDepartmentsArea: floor.departments
        .sort(compareDepartmentsArea)
        .filter((d) => d.areaSqft)
        .map((d) => ({
          name: d.department?.name,
          areaSqft: d.areaSqft,
          areaSqrm: d.areaSqrm,
        })),
    }

    return {
      ...res,
      [floor.floor.id]: {
        ...newFloor,
      },
    }
  }, {})

  return result
}

function compareWorkstations(
  a: IPortfolioTooltipSpaceUsageType,
  b: IPortfolioTooltipSpaceUsageType
) {
  if (a.workstations < b.workstations) {
    return 1
  }
  if (a.workstations > b.workstations) {
    return -1
  }
  return 0
}

function compareDepartmentsAllocated(
  a: IPortfolioTooltipDepartment,
  b: IPortfolioTooltipDepartment
) {
  if (a.allocated < b.allocated) {
    return 1
  }
  if (a.allocated > b.allocated) {
    return -1
  }
  return 0
}

function compareDepartmentsUnoccupied(
  a: IPortfolioTooltipDepartment,
  b: IPortfolioTooltipDepartment
) {
  if (a.unoccupied < b.unoccupied) {
    return 1
  }
  if (a.unoccupied > b.unoccupied) {
    return -1
  }
  return 0
}

function compareDepartmentsEmployeesTotal(
  a: IPortfolioTooltipDepartment,
  b: IPortfolioTooltipDepartment
) {
  if (a.seatedCount < b.seatedCount) {
    return 1
  }
  if (a.seatedCount > b.seatedCount) {
    return -1
  }
  return 0
}

function compareDepartmentsArea(a: IPortfolioTooltipDepartment, b: IPortfolioTooltipDepartment) {
  if (a.areaSqft < b.areaSqft) {
    return 1
  }
  if (a.areaSqft > b.areaSqft) {
    return -1
  }
  return 0
}
